import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Chip, IconButton, Paper, Switch, TextField, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/cltCommand/listClear';
const POLL_MS = 4000;

type JobStatus =
  | 'idle' | 'running' | 'stopping' | 'stopped'
  | 'paused_out_of_hour' | 'paused_maintenance' | 'paused_limit' | 'paused_error';

interface JobState {
  status: JobStatus;
  message: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  counters: Record<string, number>;
  lastError: string | null;
}

interface ShotConfig { autoEnabled: boolean; offset: number; totalPerRun: number; restartAfterHours: number; lastPassAt: string }
interface SyncConfig { autoEnabled: boolean; lastClientId: number; batchSize: number; maxBatchesPerRun: number }
interface ProviderEntry { name: string; enabled: boolean; dailyLimit: number }
interface Usage { count: number; max: number }

interface Stats { savedTotal: number; clientsTotal: number; clientsMaxId: number; clientsPending: number }

interface Payload {
  maintenance: boolean;
  stats: Stats;
  shot: { state: JobState; config: ShotConfig; waitingRestart: boolean };
  sync: { state: JobState; config: SyncConfig };
  providers: {
    order: ProviderEntry[];
    usage: Record<string, Usage>;
    open: boolean;
    closedReason: string | null;
  };
}

const STATUS_LABEL: Record<JobStatus, { text: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  idle: { text: 'Parado (aguardando)', color: 'default' },
  running: { text: 'Rodando', color: 'success' },
  stopping: { text: 'Parando…', color: 'warning' },
  stopped: { text: 'Parado pelo admin', color: 'default' },
  paused_out_of_hour: { text: 'Pausado: fora do horário', color: 'warning' },
  paused_maintenance: { text: 'Pausado: CLT em manutenção', color: 'warning' },
  paused_limit: { text: 'Pausado: teto de consultas', color: 'warning' },
  paused_error: { text: 'Pausado: erros seguidos', color: 'error' },
};

const COUNTER_LABEL: Record<string, string> = {
  processed: 'Analisados',
  consulted: 'Consultados',
  consulted_v8: 'Consultados V8',
  consulted_suma_celcoin: 'Consultados Suma · Celcoin',
  consulted_suma_uy3: 'Consultados Suma · UY3',
  errors: 'Erros',
  skip_noPhone: 'Sem telefone',
  skip_recentChat: 'Conversa recente',
  skip_recentShot: 'Disparo recente',
  skip_blockRequested: 'Pediu bloqueio',
  skip_alreadyExists: 'Já consultado no provedor',
  skip_exhausted: 'Todos provedores já tentados',
  skip_inegible: 'Inelegível',
  skip_invalidAge: 'Idade fora da faixa',
  skip_invalidCpf: 'CPF inválido',
  skip_blocked_outOfHour: 'Provedor fora do horário',
  skip_blocked_rateLimited: 'Provedor no teto',
  skip_blocked_noProviders: 'Sem provedor habilitado',
  scanned: 'Clients varridos',
  saved: 'Novos CPFs salvos',
  alreadySaved: 'Já existiam',
  noData: 'Sem dados (IaTech)',
  invalidCpf: 'CPF inválido',
  duplicatedInRun: 'Duplicados na rodada',
};

const PROVIDER_LABEL: Record<string, string> = {
  v8: 'V8',
  suma_celcoin: 'Suma · Celcoin',
  suma_uy3: 'Suma · UY3',
};

const fmt = (n: number) => n.toLocaleString('pt-BR');
const pct = (part: number, total: number) => (total > 0 ? Math.min(100, Math.round((part / total) * 100)) : 0);

const cardStyle: React.CSSProperties = { padding: 16, marginBottom: 16, textAlign: 'left' };
const hint: React.CSSProperties = { fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 12 };

/** Mensagem do servidor (JSONResponse) num erro de request, ou o texto padrão. */
function errorMessage(e: any, fallback: string): string {
  try {
    const json = JSON.parse(e?.target?.responseText ?? '');
    if (json?.message) return String(json.message);
  } catch {
    // resposta sem JSON
  }
  return fallback;
}

function num(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

function StatusBlock({ state }: { state: JobState }) {
  const s = STATUS_LABEL[state.status] ?? STATUS_LABEL.idle;
  const counters = Object.entries(state.counters).filter(([, v]) => v > 0);
  return (
    <div style={{ marginBottom: 12 }}>
      <Chip label={s.text} color={s.color} size="small" />
      {state.message && <span style={{ fontSize: 12, marginLeft: 8, color: '#000000a0' }}>{state.message}</span>}
      {counters.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {counters.map(([k, v]) => (
            <Chip key={k} size="small" variant="outlined" label={`${COUNTER_LABEL[k] ?? k}: ${v}`} />
          ))}
        </div>
      )}
      {state.lastError && (
        <div style={{ fontSize: 12, color: '#c62828', marginTop: 6 }}>Último erro: {state.lastError}</div>
      )}
    </div>
  );
}

export function ListClearPanel() {
  const [data, setData] = useState<Payload | null>(null);
  const [busy, setBusy] = useState('');
  // Campos editáveis. "dirty" evita o polling sobrescrever o que o admin está digitando.
  const [offset, setOffset] = useState('');
  const [total, setTotal] = useState('');
  const [lastId, setLastId] = useState('');
  const [batch, setBatch] = useState('');
  const [maxBatches, setMaxBatches] = useState('');
  const [restartH, setRestartH] = useState('');
  // Teto diário digitado por provedor (texto, para permitir campo vazio durante a edição).
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [order, setOrder] = useState<ProviderEntry[]>([]);
  const dirty = useRef<Record<string, boolean>>({});

  const apply = useCallback((p: Payload, force = false) => {
    setData(p);
    const d = dirty.current;
    if (force || !d.offset) setOffset(String(p.shot.config.offset));
    if (force || !d.total) setTotal(String(p.shot.config.totalPerRun));
    if (force || !d.lastId) setLastId(String(p.sync.config.lastClientId));
    if (force || !d.batch) setBatch(String(p.sync.config.batchSize));
    if (force || !d.maxBatches) setMaxBatches(String(p.sync.config.maxBatchesPerRun));
    if (force || !d.restartH) setRestartH(String(p.shot.config.restartAfterHours));
    if (force || !d.order) {
      setOrder(p.providers.order);
      setLimits(Object.fromEntries(p.providers.order.map((e) => [e.name, String(e.dailyLimit)])));
    }
    if (force) dirty.current = {};
  }, []);

  const load = useCallback(() => {
    sendServerRequest('GET', `${ENDPOINT}/status`)
      .then((e) => apply(e.data?.data))
      .catch(() => {
        /* o próximo ciclo tenta de novo; só avisa na primeira carga */
      });
  }, [apply]);

  useEffect(() => {
    sendServerRequest('GET', `${ENDPOINT}/status`)
      .then((e) => apply(e.data?.data, true))
      .catch(() => openGlobalSnack('Não foi possível carregar a limpeza de lista.', 'error'));
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [apply, load]);

  function call(key: string, path: string, body: object, okMessage: string) {
    setBusy(key);
    sendServerRequest('POST', `${ENDPOINT}/${path}`, body)
      .then((e) => {
        apply(e.data?.data, true);
        openGlobalSnack(okMessage, 'success');
      })
      .catch((e) => openGlobalSnack(errorMessage(e, 'Não foi possível concluir a ação.'), 'error'))
      .finally(() => setBusy(''));
  }

  if (!data) return <CircularLoading />;

  const mark = (k: string) => (setter: (v: string) => void) => (v: string) => {
    dirty.current[k] = true;
    setter(v);
  };
  const shotActive = data.shot.state.status === 'running' || data.shot.state.status === 'stopping';
  const syncActive = data.sync.state.status === 'running' || data.sync.state.status === 'stopping';

  function moveProvider(i: number, dir: -1 | 1) {
    const t = i + dir;
    if (t < 0 || t >= order.length) return;
    const next = [...order];
    [next[i], next[t]] = [next[t], next[i]];
    dirty.current.order = true;
    setOrder(next);
  }

  function toggleProvider(i: number, enabled: boolean) {
    dirty.current.order = true;
    setOrder(order.map((p, idx) => (idx === i ? { ...p, enabled } : p)));
  }

  function saveShotConfig() {
    const o = num(offset);
    const t = num(total);
    if (o === undefined || t === undefined || t < 1) {
      openGlobalSnack('Offset e total devem ser inteiros (total ≥ 1).', 'error');
      return;
    }
    // Só manda o offset se o admin mexeu: o servidor avança o cursor sozinho.
    const r = num(restartH);
    if (r === undefined) {
      openGlobalSnack('Espera entre voltas inválida (horas, 0 ou mais).', 'error');
      return;
    }
    call(
      'shotCfg',
      'shot/config',
      { totalPerRun: t, restartAfterHours: r, ...(dirty.current.offset && { offset: o }) },
      'Configuração salva.',
    );
  }

  function startShot() {
    const t = num(total);
    const o = num(offset);
    if (t === undefined || t < 1) {
      openGlobalSnack('Total por rodada inválido.', 'error');
      return;
    }
    call('shotStart', 'shot/start', { total: t, ...(dirty.current.offset && o !== undefined && { offset: o }) }, 'Limpeza iniciada.');
  }

  function saveSyncConfig() {
    const b = num(batch);
    const m = num(maxBatches);
    const l = num(lastId);
    if (b === undefined || b < 1 || m === undefined || m < 1 || l === undefined) {
      openGlobalSnack('Valores do sincronizador inválidos.', 'error');
      return;
    }
    call(
      'syncCfg',
      'sync/config',
      { batchSize: b, maxBatchesPerRun: m, ...(dirty.current.lastId && { lastClientId: l }) },
      'Configuração do sincronizador salva.',
    );
  }

  function saveProviders() {
    const payload: ProviderEntry[] = [];
    for (const p of order) {
      const limit = num(limits[p.name] ?? '0');
      if (limit === undefined) {
        openGlobalSnack(`Limite diário inválido em ${PROVIDER_LABEL[p.name] ?? p.name} (use 0 ou mais).`, 'error');
        return;
      }
      payload.push({ name: p.name, enabled: p.enabled, dailyLimit: limit });
    }
    call('prov', 'providers/set', { order: payload }, 'Provedores da limpeza atualizados.');
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      {data.maintenance && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          O CLT está em <b>manutenção</b>: a limpeza não inicia e a rodada atual é pausada. Ela volta sozinha
          quando a manutenção for desligada (com o cron ligado).
        </Alert>
      )}

      <Paper variant="outlined" style={{ ...cardStyle, display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'space-around', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{fmt(data.stats.savedTotal)}</div>
          <div style={{ fontSize: 12, color: '#000000a0' }}>CPFs em saved_clients (lista da limpeza)</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{fmt(data.stats.clientsTotal)}</div>
          <div style={{ fontSize: 12, color: '#000000a0' }}>registros em clients</div>
        </div>
      </Paper>

      {/* ------------------------------------------------------------ disparo */}
      <Paper variant="outlined" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 4 }}>Limpeza de lista — consulta e disparo</h3>
        <p style={hint}>
          Percorre os CPFs salvos (saved_clients) a partir do offset, consulta nos provedores abaixo e, havendo
          margem, o disparo sai para o telefone mais atual do cliente (tabela clients).
        </p>
        <StatusBlock state={data.shot.state} />
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          Posição da limpeza: <b>offset {fmt(data.shot.config.offset)}</b> de {fmt(data.stats.savedTotal)}{' '}
          ({pct(data.shot.config.offset, data.stats.savedTotal)}% da volta atual)
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Switch
            checked={data.shot.config.autoEnabled}
            disabled={busy === 'shotAuto'}
            onChange={(e) =>
              call('shotAuto', 'shot/config', { autoEnabled: e.target.checked },
                e.target.checked ? 'Cron da limpeza LIGADO.' : 'Cron da limpeza DESLIGADO.')
            }
          />
          <div>
            <b>Cron da limpeza</b>
            <div style={{ fontSize: 12, color: '#000000a0' }}>
              Ligado: roda sozinho (a cada 2 min confere), retoma do offset e respeita horário, tetos e manutenção.
              Desligado: só roda pelo botão Iniciar.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <TextField
            size="small" label="Offset (ponto de início)" value={offset} disabled={shotActive}
            onChange={(e) => mark('offset')(setOffset)(e.target.value.replace(/\D/g, ''))}
            helperText={`Posição atual no servidor: ${data.shot.config.offset}`}
          />
          <TextField
            size="small" label="Consultas por rodada" value={total}
            onChange={(e) => mark('total')(setTotal)(e.target.value.replace(/\D/g, ''))}
          />
          <TextField
            size="small" label="Espera entre voltas (horas)" value={restartH}
            onChange={(e) => mark('restartH')(setRestartH)(e.target.value.replace(/\D/g, ''))}
            helperText="Ao fim da lista volta ao 0. 0 = recomeça já"
          />
        </div>
        {data.shot.waitingRestart && (
          <Alert severity="info" style={{ marginBottom: 12 }}>
            Volta completa concluída em {new Date(data.shot.config.lastPassAt).toLocaleString('pt-BR')}. O cron
            recomeça do início depois da espera configurada (ou clique em Iniciar agora).
          </Alert>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <LoadingButton variant="contained" loading={busy === 'shotStart'} disabled={shotActive || data.maintenance} onClick={startShot}>
            Iniciar agora
          </LoadingButton>
          <LoadingButton variant="outlined" color="error" loading={busy === 'shotStop'} disabled={!shotActive}
            onClick={() => call('shotStop', 'shot/stop', {}, 'Parada solicitada.')}>
            Parar
          </LoadingButton>
          <LoadingButton variant="text" loading={busy === 'shotCfg'} onClick={saveShotConfig}>
            Salvar configuração
          </LoadingButton>
        </div>
      </Paper>

      {/* ------------------------------------------------------------ provedores */}
      <Paper variant="outlined" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 4 }}>Provedores da limpeza</h3>
        <p style={hint}>
          Ordem e tetos <b>independentes</b> da consulta do bot. Se um provedor volta sem margem, a limpeza segue
          para o próximo habilitado (com a análise offline e o horário de cada um). O teto conta só consultas de
          limpeza do dia (zera à meia-noite de Brasília; 0 = sem teto próprio, e o teto global do login continua valendo).
        </p>
        {!data.providers.open && (
          <Alert severity="info" style={{ marginBottom: 12 }}>
            Nenhum provedor da limpeza está disponível agora
            ({data.providers.closedReason === 'rateLimited' ? 'teto atingido'
              : data.providers.closedReason === 'noProviders' ? 'nenhum habilitado' : 'fora do horário'}).
          </Alert>
        )}
        {order.map((p, i) => {
          const used = data.providers.usage[p.name]?.count ?? 0;
          const max = num(limits[p.name] ?? '0') ?? 0;
          const atCap = max > 0 && used >= max;
          return (
            <Paper key={p.name} variant="outlined"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '8px 12px', marginBottom: 8, opacity: p.enabled ? 1 : 0.55 }}>
              <div style={{ minWidth: 140 }}>
                <b>{i + 1}º</b>&nbsp;&nbsp;{PROVIDER_LABEL[p.name] ?? p.name}
                {atCap && <Chip label="teto de hoje atingido" size="small" color="warning" style={{ marginLeft: 8 }} />}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TextField
                  size="small" style={{ width: 150 }} label="Limite diário"
                  value={limits[p.name] ?? ''}
                  onChange={(e) => {
                    dirty.current.order = true;
                    setLimits({ ...limits, [p.name]: e.target.value.replace(/\D/g, '') });
                  }}
                  helperText={`Hoje: ${used}${max ? ' / ' + max : ' (sem teto)'}`}
                />
                <Tooltip title={p.enabled ? 'Ativo na limpeza' : 'Inativo na limpeza'}>
                  <Switch checked={p.enabled} onChange={(e) => toggleProvider(i, e.target.checked)} />
                </Tooltip>
                <IconButton size="small" disabled={i === 0} onClick={() => moveProvider(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" disabled={i === order.length - 1} onClick={() => moveProvider(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
              </div>
            </Paper>
          );
        })}
        <div style={{ height: 8 }} />
        <LoadingButton variant="contained" loading={busy === 'prov'} onClick={saveProviders}>
          Salvar provedores
        </LoadingButton>
      </Paper>

      {/* ------------------------------------------------------------ sincronizador */}
      <Paper variant="outlined" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 4 }}>Sincronizar novos CPFs (clients → saved_clients)</h3>
        <p style={hint}>
          Copia para a lista da limpeza os CPFs novos que entraram em clients, completando nome e nascimento pela
          base quando faltarem. Guarda o último id tratado e continua dali.
        </p>
        <StatusBlock state={data.sync.state} />
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          Posição do sincronizador: <b>id {fmt(data.sync.config.lastClientId)}</b> de {fmt(data.stats.clientsMaxId)}{' '}
          (maior id em clients) · faltam varrer <b>{fmt(data.stats.clientsPending)}</b> clients
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Switch
            checked={data.sync.config.autoEnabled}
            disabled={busy === 'syncAuto'}
            onChange={(e) =>
              call('syncAuto', 'sync/config', { autoEnabled: e.target.checked },
                e.target.checked ? 'Sincronização automática LIGADA.' : 'Sincronização automática DESLIGADA.')
            }
          />
          <div>
            <b>Sincronização automática</b>
            <div style={{ fontSize: 12, color: '#000000a0' }}>Ligada: roda a cada 10 min a partir do último id.</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <TextField size="small" label="Último id de client tratado" value={lastId} disabled={syncActive}
            onChange={(e) => mark('lastId')(setLastId)(e.target.value.replace(/\D/g, ''))}
            helperText={`No servidor: ${data.sync.config.lastClientId}`} />
          <TextField size="small" label="Clients por lote" value={batch}
            onChange={(e) => mark('batch')(setBatch)(e.target.value.replace(/\D/g, ''))} />
          <TextField size="small" label="Lotes por rodada" value={maxBatches}
            onChange={(e) => mark('maxBatches')(setMaxBatches)(e.target.value.replace(/\D/g, ''))} />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <LoadingButton variant="contained" loading={busy === 'syncStart'} disabled={syncActive}
            onClick={() => call('syncStart', 'sync/start', {}, 'Sincronização iniciada.')}>
            Sincronizar agora
          </LoadingButton>
          <LoadingButton variant="outlined" color="error" loading={busy === 'syncStop'} disabled={!syncActive}
            onClick={() => call('syncStop', 'sync/stop', {}, 'Parada solicitada.')}>
            Parar
          </LoadingButton>
          <LoadingButton variant="text" loading={busy === 'syncCfg'} onClick={saveSyncConfig}>
            Salvar configuração
          </LoadingButton>
          <Button variant="text" onClick={load}>Atualizar</Button>
        </div>
      </Paper>
    </div>
  );
}
